import React from 'react';
import { useGateMonitoring } from '../../hooks/useGateMonitoring';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';

interface GateMonitoringViewProps {
  eventId: string;
}

export function GateMonitoringView({ eventId }: GateMonitoringViewProps) {
  const {
    counts,
    assignment,
    entrances,
    loading,
    error,
    tap,
    undo,
    assignEntrance,
    unassignEntrance,
  } = useGateMonitoring(eventId);

  const sessionActive = counts?.hasActiveSession ?? false;
  const currentEntranceCount =
    assignment && counts
      ? counts.byEntrance.find((e) => e.entranceId === assignment.entranceId)?.count ?? 0
      : 0;

  if (loading) {
    return (
      <div style={styles.container}>
        <p style={styles.loadingText}>Loading gate monitoring...</p>
      </div>
    );
  }

  // Session status indicator
  const sessionIndicator = (
    <div style={styles.sessionBar}>
      <span
        style={{
          ...styles.statusDot,
          backgroundColor: sessionActive ? '#2e7d32' : '#d32f2f',
        }}
        aria-hidden="true"
      />
      <span style={styles.sessionLabel}>
        {sessionActive ? 'Session Active' : 'Session Stopped'}
      </span>
    </div>
  );

  // Error display
  const errorDisplay = error ? (
    <div style={styles.errorBanner} role="alert">
      {error}
    </div>
  ) : null;

  // Entrance selection view (when no entrance assigned)
  if (!assignment) {
    return (
      <div style={styles.container}>
        {sessionIndicator}
        {errorDisplay}
        <h3 style={styles.heading}>Select an Entrance to Monitor</h3>
        {!sessionActive && (
          <p style={styles.disabledNote}>
            Waiting for an organizer to start the event session...
          </p>
        )}
        <div style={styles.entranceList} role="list" aria-label="Available entrances">
          {entrances.length === 0 ? (
            <p style={styles.emptyText}>No entrances configured for this event.</p>
          ) : (
            entrances.map((entrance) => (
              <button
                key={entrance.id}
                style={{
                  ...styles.entranceCard,
                  opacity: sessionActive ? 1 : 0.5,
                  cursor: sessionActive ? 'pointer' : 'not-allowed',
                }}
                onClick={() => sessionActive && assignEntrance(entrance.id)}
                disabled={!sessionActive}
                aria-label={`Monitor ${entrance.name} entrance`}
              >
                <span style={styles.entranceName}>{entrance.name}</span>
                <span style={styles.entranceCount}>{entrance.entryCount} entries</span>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  // Tap-to-add interface (when assigned to an entrance)
  return (
    <div style={styles.container}>
      {sessionIndicator}
      {errorDisplay}

      <div style={styles.assignmentHeader}>
        <span style={styles.assignmentLabel}>Monitoring:</span>
        <span style={styles.assignmentName}>{assignment.entranceName}</span>
      </div>

      {/* Total event count */}
      <div style={styles.totalCountRow}>
        <span style={styles.totalLabel}>Event Total</span>
        <span style={styles.totalValue}>{counts?.totalCount ?? 0}</span>
      </div>

      {/* Entrance count - prominently displayed */}
      <div style={styles.countDisplay} aria-live="polite" aria-atomic="true">
        <span style={styles.countNumber}>{currentEntranceCount}</span>
        <span style={styles.countLabel}>at {assignment.entranceName}</span>
      </div>

      {/* Large tap button */}
      <button
        style={{
          ...styles.tapButton,
          opacity: sessionActive ? 1 : 0.4,
          cursor: sessionActive ? 'pointer' : 'not-allowed',
        }}
        onClick={tap}
        disabled={!sessionActive}
        aria-label="Add guest entry"
      >
        <span style={styles.tapIcon}>➕</span>
        <span style={styles.tapText}>Add Guest</span>
      </button>

      {/* Undo button */}
      <button
        style={{
          ...styles.undoButton,
          opacity: sessionActive && currentEntranceCount > 0 ? 1 : 0.4,
          cursor: sessionActive && currentEntranceCount > 0 ? 'pointer' : 'not-allowed',
        }}
        onClick={undo}
        disabled={!sessionActive || currentEntranceCount === 0}
        aria-label="Undo last entry"
      >
        ↩️ Undo 1
      </button>

      {/* Switch Gate / Stop Monitoring actions */}
      <div style={{ display: 'flex', gap: '10px', width: '100%', maxWidth: '320px', marginTop: '8px' }}>
        <button
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '10px',
            border: `1px solid ${BORDER_LIGHT}`,
            backgroundColor: BG_SURFACE,
            color: TEXT_DARK,
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={unassignEntrance}
          aria-label="Switch to a different gate"
        >
          🔄 Switch Gate
        </button>
        <button
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '10px',
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2',
            color: '#b91c1c',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={unassignEntrance}
          aria-label="Stop monitoring this gate"
        >
          ⏹ Stop Monitoring
        </button>
      </div>

      {/* Session stopped overlay message */}
      {!sessionActive && (
        <div style={styles.stoppedOverlay} role="status">
          <p style={styles.stoppedText}>
            Event session has ended. Tap interface disabled.
          </p>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    maxWidth: '480px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  loadingText: {
    color: TEXT_MUTED,
    fontSize: '1rem',
    textAlign: 'center',
    padding: '40px 0',
  },
  sessionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    width: '100%',
    justifyContent: 'center',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  sessionLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  errorBanner: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    fontSize: '0.85rem',
    textAlign: 'center',
  },
  heading: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    fontSize: '1.25rem',
    color: TEXT_DARK,
    margin: '8px 0 4px',
    textAlign: 'center',
  },
  disabledNote: {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    textAlign: 'center',
    margin: 0,
  },
  entranceList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  entranceCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderRadius: '10px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_CARD,
    fontSize: '1rem',
    fontWeight: 500,
    color: TEXT_DARK,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    width: '100%',
    textAlign: 'left',
  },
  entranceName: {
    fontWeight: 600,
    color: TEXT_DARK,
  },
  entranceCount: {
    fontSize: '0.85rem',
    color: TEXT_SECONDARY,
  },
  emptyText: {
    color: TEXT_MUTED,
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  assignmentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    justifyContent: 'center',
  },
  assignmentLabel: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  assignmentName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: PRIMARY,
  },
  totalCountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '8px 16px',
    borderRadius: '8px',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
  },
  totalLabel: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  totalValue: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  countDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '24px 0',
  },
  countNumber: {
    fontSize: '4rem',
    fontWeight: 800,
    color: PRIMARY,
    lineHeight: 1,
    fontFamily: FONT_HEADING,
  },
  countLabel: {
    fontSize: '0.9rem',
    color: TEXT_SECONDARY,
  },
  tapButton: {
    width: '100%',
    maxWidth: '320px',
    padding: '28px 24px',
    borderRadius: '16px',
    border: 'none',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    fontSize: '1.4rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    transition: 'transform 0.1s, opacity 0.15s',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
  },
  tapIcon: {
    fontSize: '2rem',
  },
  tapText: {
    fontSize: '1.3rem',
  },
  undoButton: {
    padding: '12px 24px',
    borderRadius: '10px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  stoppedOverlay: {
    width: '100%',
    padding: '16px',
    borderRadius: '10px',
    backgroundColor: '#fef9c3',
    border: '1px solid #fde047',
    textAlign: 'center',
  },
  stoppedText: {
    margin: 0,
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#92400e',
  },
};
